import moment from "moment";
import { useIntl } from "react-intl";
import { Constants } from "../../../../constants";
import { IBaseEcg } from "../../../../models/base-ecg.interface";
import styles from "./CompareEcgs.module.css";
import Select, {
  components,
  DropdownIndicatorProps,
  MultiValue,
  OptionProps,
  SingleValue,
} from "react-select";
import iconRegular from "../../../../assets/ecgs/regular_ecg.svg";
import iconHasSymptoms from "../../../../assets/ecgs/symptomatic_ecg.svg";
import iconUnreadable from "../../../../assets/ecgs/unreadable_ecg.svg";
import { useEffect, useState } from "react";

type CompareEcgsProps = {
  ecgs?: IBaseEcg[];
  selected?: string | null;
  onChange?: (id: string) => void;
  disabled?: boolean;
};

const getEcgIcon = (hasSympoms: boolean, isUnreadable: boolean = false) => (
  <img
    style={{ transform: "scale(.8) translateX(-50%)" }}
    src={
      isUnreadable ? iconUnreadable : hasSympoms ? iconHasSymptoms : iconRegular
    }
  />
);

type CompareOptionType = {
  value: string;
  label: string;
  icon: JSX.Element;
};

const CompareEcgs = ({
  ecgs,
  selected,
  onChange,
  disabled,
}: CompareEcgsProps) => {
  const intl = useIntl();

  const changeHandler = (
    newVal: MultiValue<CompareOptionType> | SingleValue<CompareOptionType>
  ) => {
    if (onChange && newVal && "value" in newVal) onChange(newVal.value);
  };

  const compareOptions: CompareOptionType[] =
    ecgs?.map((ecg) => ({
      value: ecg.id,
      label: moment(ecg.date).format(Constants.DATE_TIME_FORMAT),
      icon: getEcgIcon(ecg.hasSymptoms!, ecg.isUnreadable!),
    })) ?? [];

  const comparePlaceholder = intl.messages["ECG_DETAILS.COMPARE"] as string;

  const Option = ({
    innerProps,
    innerRef,
    data,
  }: OptionProps<CompareOptionType>) => {
    const [isOptionHovered, setIsOptionHovered] = useState(false);
    const handleMouseEnter = () => setIsOptionHovered(true);
    const handleMouseLeave = () => setIsOptionHovered(false);
    return (
      <div
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        ref={innerRef}
        {...innerProps}
        style={{
          display: "flex",
          alignItems: "center",
          backgroundColor: isOptionHovered ? "#BCCAFA" : "unset",
          cursor: "pointer",
          marginBottom: "3px",
        }}
      >
        <div className="col-1">{data.icon}</div>
        <div className="col-11" style={{ paddingLeft: "4px" }}>
          <span style={{ color: "#000000" }}>{data.label}</span>
        </div>
      </div>
    );
  };
  const DropdownIndicator = (
    props: DropdownIndicatorProps<CompareOptionType>
  ) => {
    return (
      <components.DropdownIndicator {...props}>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            fill-rule="evenodd"
            clip-rule="evenodd"
            d="M5 7L10 12L15 7H5Z"
            fill="black"
          />
        </svg>
      </components.DropdownIndicator>
    );
  };

  const selectedOption = compareOptions.find((item) => item.value === selected);

  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  const updateScreenWidth = () => setScreenWidth(window.innerWidth);
  useEffect(() => {
    window.addEventListener("resize", updateScreenWidth);
    return () => window.removeEventListener("resize", updateScreenWidth);
  }, []);

  return (
    <Select
      value={selectedOption ?? null}
      className={`${styles.compareInput}`}
      isSearchable={false}
      options={compareOptions}
      onChange={changeHandler}
      isDisabled={!!disabled}
      placeholder={comparePlaceholder}
      components={{ Option, DropdownIndicator }}
      styles={{
        control: (provided, state) => ({
          ...provided,
          height: "100%",
          minHeight: "auto",
          cursor: "pointer",
          borderRadius: 0,
          borderColor: state.isFocused ? "#BCCAFA !important" : "white",
          boxShadow: state.isFocused ? "0 0 0 1px #BCCAFA !important" : "none",
        }),
        placeholder: (provided) => ({
          ...provided,
          height: "100%",
          cursor: "pointer",
        }),
        valueContainer: (provided) => ({
          ...provided,
          display: "unset",
          cursor: "default",
        }),
        singleValue: (provided) => ({
          ...provided,
          cursor: "pointer",
        }),
        indicatorsContainer: (provided) => ({
          ...provided,
          height: "100%",
        }),
        menuList: (provided) => ({
          ...provided,
          overflowX: "hidden",
          maxHeight: "300px",
        }),
        menu: (provided) => {
          const style = {
            ...provided,
            fontSize: "0.8rem",
          };
          if (screenWidth < 1000) {
            style.width = "200%";
            style.transform = "translateX(-50%)";
          }
          return style;
        },
      }}
    />
  );
};

export default CompareEcgs;
